import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useUser } from '../contexts/UserContext';
import { activityLogService } from '../services/activityLogService';
import { getOrCreateReferralLink } from '../services/referralService';
import QRCode from 'react-qr-code';

// PDF resources configuration - files are in the public folder
const PDF_RESOURCES = [
  {
    filename: 'FosterwayPoster2 2025.pdf',
    displayName: 'Fosterway Poster 2025',
    url: '/FosterwayPoster2 2025.pdf',
  },
  {
    filename: 'QR Code Poster 2025.pdf',
    displayName: 'QR Code Poster 2025',
    url: '/QR Code Poster 2025.pdf',
  },
  {
    filename: 'Researching Post-secondary Options Slide 2025.pdf',
    displayName: 'Researching Post-secondary Options Slide 2025',
    url: '/Researching Post-secondary Options Slide 2025.pdf',
  },
];

const Resources: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: userData } = useUser();
  const [previewPdf, setPreviewPdf] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [shortenedLink, setShortenedLink] = useState('');
  const [loadingLink, setLoadingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleDownload = async (filename: string, pdfUrl: string) => {
    if (!userData?.uid) {
      console.error('User not authenticated');
      return;
    }

    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Log the download activity
      await activityLogService.logActivity({
        timestamp: Date.now(),
        uid: userData.uid,
        actionTaken: 'Download',
        actionSubject: filename,
      });

      console.log('Download logged successfully:', filename);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handlePreview = (pdfUrl: string) => {
    setPreviewPdf(pdfUrl);
  };

  const handleClosePreview = () => {
    setPreviewPdf(null);
  };

  // Generate referral link on component mount
  useEffect(() => {
    const generateShortLink = async () => {
      if (!userData?.uid) {
        return;
      }
      
      setLoadingLink(true);
      setLinkError(null);
      
      try {
        const shortLink = await getOrCreateReferralLink(userData.uid);
        setShortenedLink(shortLink);
      } catch (err) {
        console.error('Error creating short link:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load referral link. Please try again.';
        setLinkError(errorMessage);
      } finally {
        setLoadingLink(false);
      }
    };

    generateShortLink();
  }, [userData?.uid]);

  const handleCopyLink = async () => {
    if (!shortenedLink) return;
    
    try {
      await navigator.clipboard.writeText(shortenedLink);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  const handleRetry = () => {
    if (!userData?.uid) return;
    
    setLoadingLink(true);
    setLinkError(null);
    
    getOrCreateReferralLink(userData.uid)
      .then(setShortenedLink)
      .catch((err) => {
        console.error('Error creating short link:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load referral link. Please try again.';
        setLinkError(errorMessage);
      })
      .finally(() => setLoadingLink(false));
  };

  const handleDownloadQR = () => {
    // Create a canvas to render the QR code for download
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = 300;
    canvas.width = size;
    canvas.height = size;

    if (ctx) {
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);

      // Create QR code SVG
      const svg = document.querySelector('svg[data-testid="qr-code"]') as SVGElement;
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, size, size);
          
          // Download
          const link = document.createElement('a');
          link.download = 'fosterway-referral-qr.png';
          link.href = canvas.toDataURL();
          link.click();
          
          URL.revokeObjectURL(svgUrl);
        };
        img.src = svgUrl;
      }
    }
  };

  const isGuidanceUser = userData?.isGuidanceUser || false;

  // QR Code Section Component
  const QRCodeSection = () => (
    shortenedLink ? (
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Card 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            border: '2px solid #e9ecef',
            borderRadius: 2,
            maxWidth: 300
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <QrCodeIcon sx={{ mr: 1, color: '#2e7d32' }} />
              <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                Scan to Share
              </Typography>
            </Box>
            
            {/* QR Code with Fosterway styling */}
            <Box 
              sx={{ 
                position: 'relative',
                display: 'inline-block',
                p: 2,
                backgroundColor: 'white',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <QRCode
                value={shortenedLink}
                size={200}
                style={{ 
                  height: 'auto',
                  maxWidth: '100%',
                  width: '100%'
                }}
                fgColor="#1976d2" // Fosterway blue for main QR code
                bgColor="#ffffff"
              />
            </Box>
            
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 2, 
                color: '#666',
                fontSize: '12px',
                mb: 2
              }}
            >
              Scan with your phone camera to share this referral link
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', width: '100%' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadQR}
                size="small"
                fullWidth
                sx={{
                  borderColor: '#2e7d32',
                  color: '#2e7d32',
                  '&:hover': {
                    borderColor: '#1b5e20',
                    backgroundColor: '#f1f8e9'
                  }
                }}
              >
                Download QR Code
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyLink}
                size="small"
                fullWidth
                disabled={!shortenedLink}
                sx={{
                  borderColor: '#2e7d32',
                  color: '#2e7d32',
                  '&:hover': {
                    borderColor: '#1b5e20',
                    backgroundColor: '#f1f8e9'
                  }
                }}
              >
                Copy Link
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    ) : null
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          mb: 4, 
          fontWeight: 600,
          color: theme.palette.text.primary 
        }}
      >
        {isGuidanceUser ? 'Guidance Resources' : 'Resources'}
      </Typography>

      {/* QR Code Section - Show at top for non-Guidance users */}
      {!isGuidanceUser && <QRCodeSection />}

      {/* Loading and Error States for Referral Link */}
      {loadingLink && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, p: 2, mb: 4 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading referral link...
          </Typography>
        </Box>
      )}
      
      {linkError && (
        <Box sx={{ p: 2, textAlign: 'center', mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {linkError}
          </Alert>
          <Button variant="outlined" onClick={handleRetry}>
            Try Again
          </Button>
        </Box>
      )}

      {/* Guidance Resources heading - only show for non-Guidance users */}
      {!isGuidanceUser && (
        <Typography 
          variant="h5" 
          component="h2"
          sx={{ 
            mb: 3, 
            mt: 4,
            fontWeight: 600,
            color: theme.palette.text.primary 
          }}
        >
          Guidance Resources
        </Typography>
      )}

      <Typography 
        variant="body1" 
        sx={{ 
          mb: 4, 
          color: theme.palette.text.secondary,
          maxWidth: '800px'
        }}
      >
        Below are some posters and slide templates to support your outreach efforts to students. Click on any resource to preview it, or download it directly. Please reach out to us at support@fosterway.com to let us know if you have questions, comments or suggestions about these resources.
      </Typography>

      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 3,
          mb: 4
        }}
      >
        {PDF_RESOURCES.map((resource) => (
          <Card 
            key={resource.filename}
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PictureAsPdfIcon sx={{ fontSize: 40, color: theme.palette.error.main, mr: 2 }} />
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontWeight: 500,
                    flexGrow: 1,
                    wordBreak: 'break-word'
                  }}
                >
                  {resource.displayName}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handlePreview(resource.url)}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: theme.palette.primary.light + '20',
                    },
                  }}
                >
                  Preview
                </Button>
                <Tooltip title="Download PDF">
                  <IconButton
                    onClick={() => handleDownload(resource.filename, resource.url)}
                    sx={{
                      border: `1px solid ${theme.palette.primary.main}`,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: theme.palette.primary.light + '20',
                      },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* QR Code Section - Show below PDF links for Guidance users */}
      {isGuidanceUser && <QRCodeSection />}

      {/* PDF Preview Dialog */}
      <Dialog
        open={!!previewPdf}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : '90vh',
            maxHeight: isMobile ? '100%' : '90vh',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">PDF Preview</Typography>
          <IconButton onClick={handleClosePreview} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {previewPdf && (
            <iframe
              src={previewPdf}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="PDF Preview"
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Resources;

